export type EngineStatus = 'idle' | 'loading' | 'ready' | 'failed'

export interface HomeState {
    scene: number
    copyScene: number
    copyPhase: 'in' | 'out'
    statsScene: number
    statsEpoch: number
    labelScene: number
    labelEpoch: number
    settled: boolean
    paused: boolean
    engine: EngineStatus
}

export interface FrameInfo {
    yaw: number
    pitch: number
    progress: number
    scene: number
    transitioning: boolean
}

const initialState: HomeState = {
    scene: 0,
    copyScene: 0,
    copyPhase: 'in',
    statsScene: 0,
    statsEpoch: 0,
    labelScene: 0,
    labelEpoch: 0,
    settled: true,
    paused: false,
    engine: 'idle'
}

let state: HomeState = initialState

const listeners = new Set<() => void>()

/**
 * UI state shared by the Home page and the point-cloud engine. Lives at module scope so the
 * current scene survives navigating away from Home and back.
 */
export const homeStore = {
    get: (): HomeState => state,
    getServer: (): HomeState => initialState,
    set: (patch: Partial<HomeState>): void => {
        state = { ...state, ...patch }
        listeners.forEach(listener => listener())
    },
    subscribe: (listener: () => void): (() => void) => {
        listeners.add(listener)

        return () => {
            listeners.delete(listener)
        }
    }
}

const frameListeners = new Set<(info: FrameInfo) => void>()

export const frameInfo: FrameInfo = {
    yaw: 36,
    pitch: 32,
    progress: 0,
    scene: 0,
    transitioning: false
}

export const frameBus = {
    emit: (): void => {
        frameListeners.forEach(listener => listener(frameInfo))
    },
    subscribe: (listener: (info: FrameInfo) => void): (() => void) => {
        frameListeners.add(listener)

        return () => {
            frameListeners.delete(listener)
        }
    }
}

export interface HomeCommands {
    goTo: (index: number) => void
    next: () => void
    prev: () => void
    togglePause: () => void
    setPaused: (paused: boolean) => void
    setHover: (hover: boolean) => void
}

let engineCommands: HomeCommands | null = null

export const bindCommands = (commands: HomeCommands | null): void => {
    engineCommands = commands
}

const wrap = (index: number): number => ((index % 4) + 4) % 4

/**
 * Commands used by the UI. Without a running engine (WebGL unavailable) they switch the copy directly.
 */
export const homeCommands: HomeCommands = {
    goTo: (index: number): void => {
        if (engineCommands) {
            engineCommands.goTo(index)

            return
        }

        const target = wrap(index)

        homeStore.set({
            scene: target,
            copyScene: target,
            copyPhase: 'in',
            statsScene: target,
            statsEpoch: state.statsEpoch + 1,
            labelScene: target,
            labelEpoch: state.labelEpoch + 1
        })
    },
    next: (): void => {
        if (engineCommands) engineCommands.next()
        else homeCommands.goTo(state.scene + 1)
    },
    prev: (): void => {
        if (engineCommands) engineCommands.prev()
        else homeCommands.goTo(state.scene - 1)
    },
    togglePause: (): void => {
        if (engineCommands) engineCommands.togglePause()
        else homeStore.set({ paused: !state.paused })
    },
    setPaused: (paused: boolean): void => {
        if (engineCommands) engineCommands.setPaused(paused)
        else homeStore.set({ paused })
    },
    setHover: (hover: boolean): void => {
        engineCommands?.setHover(hover)
    }
}
